/// <amd-module name="Release/Landing/LeftPanel/AllPipelines/DefinitionsTreeProvider" />
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageContext } from "VSS/Platform/Context";
import { ObservableValue } from "VSS/Core/Observable";
import { TreeItemProvider as TreeItemExProvider, ITreeItemProvider as ITreeItemExProvider, ITreeItem as ITreeItem2, ITreeItemAdd, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";

import { Folder, ReleaseDefinition } from "Release/Client/Contracts/Release";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { INode, IAllDefinitionsProviderData, NodeType } from "./Types";
import { FolderUtils } from "./FolderUtils";
import { RmServiceInstanceId } from "Constants";
import { AllDefinitionsActions } from "./AllDefinitionsActions";
import { MessageActions } from "Release/Landing/HubMessageBar/MessageActions";
import { MessageBarKeys } from "Release/Landing/Constants";
import { IReleaseHubServiceDataService } from "PageData";
import { IActiveDefinitionReference } from "DataProviderContracts";

class BaseDefinitionsTreeProvider {
    constructor(protected _pageContext: IVssPageContext) { }

    protected _getChildItems(parentFolder: Folder, updatingStore: boolean = false): INode[] | Promise<INode[]> {
        if (FolderUtils.isRootPath(parentFolder.path)) {
            const pageDataService = this._pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
            const folders = pageDataService.getFolders();
            const pipelines = pageDataService.getRootReleaseDefinitions();

            if (folders && pipelines) {
                return this._handleInitialData(parentFolder, folders, pipelines);
            }
            else {
                if (!updatingStore) {
                    // If the store is updating (cases where a pipeline under root is renamed or a folder is created), we are in the context of an action
                    // Therefore put a timeout to get out of the context
                    ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions).fetchingPipelines.invoke({});
                }
            }
        }

        const parameters: { [key: string]: string } = {};
        parameters["folderPath"] = parentFolder.path;

        return this._contributionSource.getDataAsync(dataProviderContributionId, parameters, true, RmServiceInstanceId)
            .then((data: IAllDefinitionsProviderData) => {
                if (data.errorMessage) {
                    this._handleError(data.errorMessage);
                    ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions).pipelinesFetched.invoke({});
                    return [];
                }

                return this._getChildItemsFromResponse(parentFolder, data.folders, data.releaseDefinitions);
            },
                (error) => {
                    this._handleError(error);
                    ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions).pipelinesFetched.invoke({});
                    return Promise.resolve([]);
                });
    }

    protected _handleError(error: any): void {
        let errorMessage: string = error ? error.message || error : "";
        ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubError, value: errorMessage });
    }

    protected _createRootFolder(): INode[] {
        let rootFolder = {
            path: FolderUtils.RootFolderPath
        } as Folder;

        return [{
            type: NodeType.Folder,
            folder: rootFolder,
            releaseDefinition: {},
            isExpandable: true,
            defaultIsExpanded: true
        }] as INode[];
    }

    protected get _contributionSource(): IVssContributionService {
        return this._pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    protected _getChildItemsFromResponse(parentFolder: Folder, folders: Folder[], pipelines: IActiveDefinitionReference[]): INode[] {

        let childItems: INode[] = [];

        // The Folders API returns the queried parent folder along with the child folders. Removing the parent folder here.
        if (folders.length > 0 && folders[0].path === parentFolder.path) {
            folders.shift();
        }

        let childFolders: INode[] = folders.filter(f => FolderUtils.getParentFolderPath(f).toLowerCase() === parentFolder.path.toLowerCase()).map(f => FolderUtils.convertFolder(f, false));
        childItems.push(...childFolders);

        let childDefinitions: INode[] = pipelines.filter(rd => rd.path.toLowerCase() === parentFolder.path.toLowerCase()).map(rd => FolderUtils.convertDefinitionReference(rd));
        childItems.push(...childDefinitions);

        return childItems;
    }

    protected _handleInitialData(parentFolder: Folder, folders: Folder[], pipelines: IActiveDefinitionReference[]): INode[] {
        return [];
    }
}

type ObservableNode = ObservableValue<INode>;
type TreeExNode = ITreeItem2<ObservableNode>;

export class DefinitionsTreeExProvider extends BaseDefinitionsTreeProvider implements ITreeItemExProvider<ObservableNode> {
    // Set of root items for the tree.
    private rootItems: ITreeItem2<ObservableNode>[];

    public constructor(
        pageContext: IVssPageContext,
        private _pathToChildrenCache: { [key: string]: INode[] | Promise<INode[]> },
        private _pathToChildrenCache2: { [key: string]: INode[] },
        private _pathToExpandedCache: { [key: string]: boolean },
        private _updatingStore: boolean = false) {

        super(pageContext);

        this._treeItemProvider = new TreeItemExProvider([]);
        this._addRootItems();
    }

    public get roots(): ITreeItem2<ObservableNode>[] {
        return this.rootItems;
    }

    public add(item: TreeExNode, parentItem?: TreeExNode, insertAfter?: TreeExNode) {
        if (this._treeItemProvider) {
            this._treeItemProvider.add(item, parentItem, insertAfter)
        }
    }

    public clear() {
        if (this._treeItemProvider) {
            this._treeItemProvider.clear();
        }
    }

    public remove(treeItem: TreeExNode, parentItem?: TreeExNode) {
        if (this._treeItemProvider) {
            this._treeItemProvider.remove(treeItem, parentItem);
        }
    }

    public splice(parentItem: TreeExNode | undefined, itemsToRemove?: TreeExNode[], itemsToAdd?: ITreeItemAdd<ObservableNode>[]) {
        if (this._treeItemProvider) {
            this._treeItemProvider.splice(parentItem, itemsToRemove, itemsToAdd);
        }
    }


    public toggle(treeItem: TreeExNode) {
        const node = treeItem.data.value;
        if (!FolderUtils.isFolder(node)) {
            return;
        }

        if (!treeItem.expanded) {
            // treeItem.expanded is false which means that the item is being expanded
            if (!this._pathToChildrenCache[node.folder.path]) {
                this._getChildren(node);
            }
        }

        if (this._treeItemProvider) {
            this._pathToExpandedCache[treeItem.data.value.folder.path] = !treeItem.expanded;
            this._treeItemProvider.toggle(treeItem);
            ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions).folderToggled.invoke({});
        }
    }

    public get length() {
        return this._treeItemProvider.length;
    }

    public get value(): ITreeItemEx<ObservableNode>[] {
        return this._treeItemProvider.value;
    }

    protected _handleInitialData(parentFolder: Folder, folders: Folder[], pipelines: IActiveDefinitionReference[]): INode[] {

        let childItems: INode[] = [];

        // The Folders API returns the queried parent folder along with the child folders. Removing the parent folder here.
        if (folders.length > 0 && folders[0].path === parentFolder.path) {
            folders.shift();
        }

        const childFolders = folders.filter(f => FolderUtils.getParentFolderPath(f).toLowerCase() === parentFolder.path.toLowerCase());
        let childFolderNodes: INode[] = childFolders.map(f => FolderUtils.convertFolder(f, false));
        childItems.push(...childFolderNodes);
        if (childFolders.length) {
            childFolders.forEach(cf => {
                const childItems = this._handleInitialData(cf, folders, pipelines);
                if (childItems && childItems.length) {
                    this._pathToChildrenCache[cf.path] = childItems;
                    this._pathToChildrenCache2[cf.path] = childItems;
                    this._pathToExpandedCache[cf.path] = true;
                }
            });
        }

        let childDefinitions: INode[] = pipelines.filter(rd => rd.path.toLowerCase() === parentFolder.path.toLowerCase()).map(rd => FolderUtils.convertDefinitionReference(rd));
        childItems.push(...childDefinitions);

        return childItems;
    }

    private _getChildren(item: INode): INode[] | Promise<INode[]> {
        if (!item) {
            // Create RootFolder item
            return [];
        }
        else if (!FolderUtils.isFolder(item)) {
            return [];
        }

        let cacheKey: string = item.folder.path;
        if (!this._pathToChildrenCache[cacheKey]) {
            this._pathToChildrenCache[cacheKey] = this._getChildItems(item.folder, this._updatingStore);
            Promise.resolve(this._pathToChildrenCache[cacheKey]).then((nodes: INode[]) => {
                this._pathToChildrenCache2[cacheKey] = nodes;
                ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions).pipelinesFetched.invoke({});
            });
        }

        return this._pathToChildrenCache[cacheKey];
    }

    private _getTreeItem(item?: INode, childItems?: TreeExNode[]): TreeExNode {
        return {
            data: new ObservableValue(item),
            childItems: childItems,
            expanded: false
        } as TreeExNode;
    }

    private _addRootItems(): void {
        this.clear();
        const rootNode = this._createRootFolder()[0];
        if (this._pathToExpandedCache[rootNode.folder.path] !== false) {
            this._pathToExpandedCache[rootNode.folder.path] = true;
        }

        const rootTreeItem = {
            data: new ObservableValue(rootNode),
            expanded: this._pathToExpandedCache[rootNode.folder.path]
        };

        this.rootItems = [rootTreeItem];
        this.add(rootTreeItem);
        this._addChildItemsToFolderNode(rootTreeItem, true);
    }

    private _addChildItemsToFolderNode(treeItem: TreeExNode, recurse?: boolean) {
        const node = treeItem.data.value;
        if (!FolderUtils.isFolder(node)) {
            return [];
        }

        const recurseIfNeeded = (treeItemsToAdd: TreeExNode[]) => {
            if (recurse) {
                if (!treeItemsToAdd.length) {
                }
                else {
                    treeItemsToAdd.forEach(item => {
                        const childNode = item.data.value;
                        if (!!childNode.folder && this._pathToExpandedCache[childNode.folder.path]) {
                            item.expanded = true;
                            this._addChildItemsToFolderNode(item, recurse);
                        }
                    });
                }
            }
            else {
            }
        }

        const addChildNodes = (childNodes: INode[]) => {
            const treeItemsToAdd = childNodes.map(cn => this._getTreeItem(cn));
            treeItem.childItems = treeItemsToAdd;
            this.splice(treeItem, undefined, [{ items: treeItemsToAdd }]);
            recurseIfNeeded(treeItemsToAdd);
        }

        if (!treeItem.childItems) {
            let childNodes: INode[] = this._pathToChildrenCache2[node.folder.path];
            if (!childNodes && this._pathToExpandedCache[node.folder.path]) {
                let fetchedChildNodes: INode[] | Promise<INode[]> | undefined = undefined;
                if (!this._pathToChildrenCache[node.folder.path]) {
                    fetchedChildNodes = this._getChildren(node);
                }

                if (Array.isArray(fetchedChildNodes)) {
                    addChildNodes(fetchedChildNodes);
                }
                else {
                    const loadingItem = this._getTreeItem();
                    treeItem.childItems = [loadingItem];
                    this.splice(treeItem, undefined, [{ items: [loadingItem] }]);
                }
            }
            else if (Array.isArray(childNodes)) {
                addChildNodes(childNodes);
            }
        }
    }

    private _treeItemProvider: TreeItemExProvider<ObservableNode>;
}

const dataProviderContributionId: string = "ms.vss-releaseManagement-web.all-definitions-lwp-data-provider";
